import React, { useEffect, useState } from 'react';
import { Button } from '../UI/Button';
import { AgentBot } from '../../types/agentBot';
import { Inbox } from '../../types/inbox';
import { SupportGroup } from '../../types/supportGroup';
import { UpdateBotActuationRequest } from '../../types/botActuation';
import { agentBotService } from '../../services/agentBotService';
import { useToast } from '../UI/ToastProvider';
import { apiErrorMessage } from '../../utils/apiErrorMessage';

interface AgentBotActuationSectionProps {
  bot: AgentBot;
  inboxes: Inbox[];
  groups: SupportGroup[];
}

const defaultForm: UpdateBotActuationRequest = {
  autoHandoffEnabled: true,
  defaultSupportGroupId: null,
  historyLimit: 10,
  includeHumanHistory: true,
  systemInstructions: '',
  handoffTriggerKeywords: '',
};

export const AgentBotActuationSection: React.FC<AgentBotActuationSectionProps> = ({
  bot,
  inboxes,
  groups,
}) => {
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [scope, setScope] = useState<'bot' | 'inbox'>('bot');
  const [selectedInboxId, setSelectedInboxId] = useState('');
  const [form, setForm] = useState<UpdateBotActuationRequest>(defaultForm);

  const linkedInboxes = inboxes.filter(inbox => inbox.agentBotId === bot.id);

  useEffect(() => {
    setScope('bot');
    setSelectedInboxId('');
    void loadActuation(bot.id);
  }, [bot.id]);

  const loadActuation = async (botId: string, inboxId?: string) => {
    setLoading(true);
    try
    {
      const data = await agentBotService.getActuation(botId, inboxId);
      setForm({
        autoHandoffEnabled: data.autoHandoffEnabled,
        defaultSupportGroupId: data.defaultSupportGroupId ?? null,
        historyLimit: data.historyLimit,
        includeHumanHistory: data.includeHumanHistory,
        systemInstructions: data.systemInstructions ?? '',
        handoffTriggerKeywords: data.handoffTriggerKeywords ?? '',
      });
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Erro ao carregar regras de atuação'), 'error');
    }
    finally
    {
      setLoading(false);
    }
  };

  const handleScopeChange = (newScope: 'bot' | 'inbox') => {
    setScope(newScope);
    if (newScope === 'bot')
    {
      void loadActuation(bot.id);
      return;
    }

    const firstInbox = linkedInboxes[0]?.id ?? '';
    setSelectedInboxId(firstInbox);
    if (firstInbox)
    {
      void loadActuation(bot.id, firstInbox);
    }
  };

  const handleInboxChange = (inboxId: string) => {
    setSelectedInboxId(inboxId);
    if (inboxId)
    {
      void loadActuation(bot.id, inboxId);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (scope === 'inbox' && !selectedInboxId)
    {
      addToast('Selecione uma caixa de entrada', 'error');
      return;
    }

    setSaving(true);
    try
    {
      if (scope === 'inbox')
      {
        await agentBotService.updateInboxActuation(bot.id, {
          ...form,
          inboxId: selectedInboxId,
        });
      }
      else
      {
        await agentBotService.updateActuation(bot.id, form);
      }

      addToast('Regras de atuação salvas', 'success');
    }
    catch (error)
    {
      addToast(apiErrorMessage(error, 'Erro ao salvar regras de atuação'), 'error');
    }
    finally
    {
      setSaving(false);
    }
  };

  if (loading)
  {
    return (
      <div className="flex justify-center py-8">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6 space-y-4">
      <div>
        <div className="flex gap-4 border-b pb-3">
          <button
            type="button"
            className={`text-sm font-medium ${scope === 'bot' ? 'text-blue-900 border-b-2 border-blue-900' : 'text-gray-500'}`}
            onClick={() => handleScopeChange('bot')}
          >
            Robô (global)
          </button>
          <button
            type="button"
            className={`text-sm font-medium ${scope === 'inbox' ? 'text-blue-900 border-b-2 border-blue-900' : 'text-gray-500'} disabled:opacity-50 disabled:cursor-not-allowed`}
            onClick={() => handleScopeChange('inbox')}
            disabled={linkedInboxes.length === 0}
            title={linkedInboxes.length === 0 ? 'Vincule o robô a uma caixa para configurar um override' : undefined}
          >
            Por caixa de entrada
          </button>
        </div>
        <p className="mt-2 text-xs text-gray-500">
          {scope === 'bot'
            ? 'Padrão de todas as caixas em que este robô está vinculado.'
            : 'Substitui o padrão do robô só nesta caixa. Grupo, palavras-chave e instruções vazios continuam herdando o global.'}
        </p>
        {linkedInboxes.length === 0 && (
          <p className="mt-1 text-xs text-gray-500">
            Nenhuma caixa vinculada a este robô. A aba por caixa fica disponível depois do vínculo.
          </p>
        )}
      </div>

      {scope === 'inbox' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Caixa de entrada</label>
          <select
            value={selectedInboxId}
            onChange={e => handleInboxChange(e.target.value)}
            className="w-full border rounded-md px-3 py-2 text-sm"
          >
            <option value="">Selecione...</option>
            {linkedInboxes.map(inbox => (
              <option key={inbox.id} value={inbox.id}>{inbox.name}</option>
            ))}
          </select>
        </div>
      )}

      <section className="space-y-3 rounded-md border border-gray-200 p-3">
        <h3 className="text-sm font-semibold text-gray-900">Transferência para humano</h3>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-gray-300"
            checked={form.autoHandoffEnabled}
            onChange={e => setForm(f => ({ ...f, autoHandoffEnabled: e.target.checked }))}
          />
          <span>
            <span className="block text-sm font-medium text-gray-900">Transferir automaticamente para humano</span>
            <span className="block text-xs text-gray-500 mt-0.5">
              O assistente detecta pedidos de atendente (ex.: “quero falar com alguém”).
              Desligado, o robô só responde e não transfere por intenção nem por palavra-chave.
            </span>
          </span>
        </label>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Palavras-chave de fallback (separadas por vírgula)
          </label>
          <input
            type="text"
            value={form.handoffTriggerKeywords ?? ''}
            onChange={e => setForm(f => ({ ...f, handoffTriggerKeywords: e.target.value }))}
            className="w-full border rounded-md px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500"
            placeholder="atendente, humano, operador"
            disabled={!form.autoHandoffEnabled}
          />
          <p className="mt-1 text-xs text-gray-500">
            Rede de segurança se o modelo falhar: transfere se a mensagem do cliente contiver algum destes termos.
            Não usa a resposta do próprio bot. Deixe vazio para depender só da detecção do assistente.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Grupo padrão para handoff</label>
          <select
            value={form.defaultSupportGroupId ?? ''}
            onChange={e => setForm(f => ({
              ...f,
              defaultSupportGroupId: e.target.value || null,
            }))}
            className="w-full border rounded-md px-3 py-2 text-sm"
          >
            <option value="">Nenhum — manter o grupo da conversa</option>
            {groups.map(group => (
              <option key={group.id} value={group.id}>{group.name}</option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            A conversa vai para a fila mesmo sem grupo padrão. “Nenhum” não impede a transferência.
          </p>
        </div>
      </section>

      <section className="space-y-3 rounded-md border border-gray-200 p-3">
        <h3 className="text-sm font-semibold text-gray-900">Contexto do assistente</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Limite de histórico (mensagens)</label>
          <input
            type="number"
            min={1}
            max={50}
            value={form.historyLimit}
            onChange={e => setForm(f => ({
              ...f,
              historyLimit: parseInt(e.target.value, 10) || 10,
            }))}
            className="w-full max-w-xs border rounded-md px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-gray-500">
            Quantas mensagens anteriores entram no contexto das respostas.
          </p>
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-gray-300"
            checked={form.includeHumanHistory}
            onChange={e => setForm(f => ({ ...f, includeHumanHistory: e.target.checked }))}
          />
          <span>
            <span className="block text-sm font-medium text-gray-900">Incluir histórico humano no contexto do bot</span>
            <span className="block text-xs text-gray-500 mt-0.5">
              Quando desligado, mensagens de operadores não entram no contexto do assistente.
            </span>
          </span>
        </label>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Instruções adicionais do sistema</label>
          <textarea
            value={form.systemInstructions ?? ''}
            onChange={e => setForm(f => ({ ...f, systemInstructions: e.target.value }))}
            className="w-full border rounded-md px-3 py-2 text-sm"
            rows={4}
            placeholder="Regras específicas de comportamento do agente..."
          />
        </div>
      </section>

      <div className="flex justify-end gap-2 pt-2">
        <Button variant="primary" type="submit" disabled={saving}>
          {saving ? 'Salvando...' : 'Salvar atuação'}
        </Button>
      </div>
    </form>
  );
};
